import { useState } from 'react';
import { getConfig, getSupabase } from '../lib/supabase';
import { C, EASE_SPRING, ink, PAGE_BG } from '../lib/theme';

/**
 * Gate de login do Hub. Usa o mesmo Supabase Auth do backend, na mesma origem —
 * a sessão fica no localStorage e o requireAuth do Express valida o JWT + domínio.
 * A checagem de domínio aqui é conveniência de UX; a barreira real é no backend.
 */
export function LoginPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; kind: 'error' | 'ok' } | null>(null);

  const domains = getConfig().allowedEmailDomains;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);

    const domain = email.trim().toLowerCase().split('@')[1];
    if (!domain || !domains.includes(domain)) {
      setMsg({ text: `Só e-mails ${domains.map((d) => `@${d}`).join(' ou ')} são aceitos.`, kind: 'error' });
      return;
    }

    setBusy(true);
    const supabase = getSupabase();
    const { data, error } = mode === 'signin'
      ? await supabase.auth.signInWithPassword({ email: email.trim(), password })
      : await supabase.auth.signUp({ email: email.trim(), password });
    setBusy(false);

    if (error) { setMsg({ text: error.message, kind: 'error' }); return; }

    if (mode === 'signup' && !data.session) {
      setMsg({ text: 'Conta criada. Se a confirmação por e-mail estiver ativa, confirme antes de entrar.', kind: 'ok' });
      return;
    }
    onSignedIn();
  }

  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', font: 'inherit', fontSize: 14, padding: '12px 15px',
    borderRadius: 12, border: `1px solid ${ink(0.14)}`, background: 'var(--input-bg)',
    color: C.fg, outline: 'none',
  };

  return (
    <div style={{
      minHeight: '100vh', background: PAGE_BG, color: C.fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative',
    }}>
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none' }}>
        <div style={{
          position: 'absolute', width: 620, height: 620, borderRadius: '50%', filter: 'blur(100px)',
          opacity: 0.3, top: -220, left: -140,
          background: `radial-gradient(circle at 40% 40%, ${C.accentStrong}, transparent 68%)`,
          animation: 'koDrift1 18s ease-in-out infinite alternate',
        }} />
      </div>

      <form onSubmit={submit} style={{
        position: 'relative', width: '100%', maxWidth: 400, padding: 32,
        background: ink(0.045), border: `1px solid ${ink(0.1)}`, borderRadius: 24,
        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        boxShadow: '0 30px 90px rgba(8,8,16,0.5)',
        animation: `koSlideIn 0.4s ${EASE_SPRING}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <img src="/assets/kory-logo.png" alt="Kory" style={{ height: 22, filter: 'drop-shadow(0 2px 10px rgba(255,107,71,0.35))' }} />
          <div style={{ width: 1, height: 20, background: ink(0.18) }} />
          <span style={{
            fontSize: 11.5, fontWeight: 750, letterSpacing: '0.19em', textTransform: 'uppercase', color: ink(0.62),
          }}>RevOps Hub</span>
        </div>

        <div style={{ fontSize: 13, color: ink(0.45), marginBottom: 22 }}>
          {mode === 'signin' ? 'Entre para auditar a fila de perda.' : 'Crie sua conta de acesso.'}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder={`voce@${domains[0] ?? 'kory.one'}`} style={input} required autoComplete="email" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Senha" style={input} required minLength={6}
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} />
        </div>

        {msg && (
          <div style={{
            marginTop: 12, fontSize: 12.5, padding: '10px 13px', borderRadius: 11,
            background: msg.kind === 'error' ? 'rgba(242,85,90,0.1)' : 'rgba(46,204,113,0.1)',
            border: `1px solid ${msg.kind === 'error' ? 'rgba(242,85,90,0.28)' : 'rgba(46,204,113,0.28)'}`,
            color: msg.kind === 'error' ? C.churn : C.green,
          }}>{msg.text}</div>
        )}

        <button type="submit" disabled={busy} style={{
          width: '100%', marginTop: 16, border: 'none', cursor: busy ? 'wait' : 'pointer', font: 'inherit',
          fontSize: 14, fontWeight: 750, padding: '12px 0', borderRadius: 12,
          background: `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})`, color: '#fff',
          boxShadow: '0 8px 24px rgba(232,85,48,0.34)', opacity: busy ? 0.6 : 1,
        }}>
          {busy ? 'Entrando…' : mode === 'signin' ? 'Entrar' : 'Criar conta'}
        </button>

        <button type="button" onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setMsg(null); }} style={{
          width: '100%', marginTop: 12, border: 'none', background: 'none', cursor: 'pointer',
          font: 'inherit', fontSize: 12.5, color: ink(0.45),
        }}>
          {mode === 'signin' ? 'Não tem conta? Criar uma' : 'Já tem conta? Entrar'}
        </button>
      </form>
    </div>
  );
}
